import React, { useEffect } from 'react';
import { useParams, useLocation, Redirect } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { redeemHash } from './redeemHash';
import { useHashView } from './useHashView';
import { IN_SCOPE_PATH_FOR, parseShareUrl } from './shareLinkConfig';

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60vh',
  gap: theme.spacing(2),
  padding: theme.spacing(4),
  textAlign: 'center',
}));

// Handler for /share/:hash. Redeems the hash via the backend (stubbed for now),
// then redirects to the canonical content path. On error, shows a simple message.
export default function ShareRedemptionRoute() {
  const { hash, kind, id } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const { scope, status } = useHashView();

  useEffect(() => {
    // Parse the FULL share URL (pathname + search) so the redeemed scope carries
    // every query param the destination route needs — for a Library document that
    // means editionId + classificationId (IN_SCOPE_PATH_FOR builds
    // /library/:edition/document/:classification from them). Falling back to a
    // hand-built { kind, id } here would drop those params and resolve the wrong
    // route (/library/<edition-document-join-id>), which renders nothing and
    // bounces an anonymous viewer to login.
    const parsed = parseShareUrl(location);
    const urlScope = parsed?.scope || { kind, id };
    dispatch(redeemHash(hash, urlScope)).catch(() => {
      // Reducer already captured the error; nothing else to do here.
    });
  }, [hash, kind, id, location, dispatch]);

  if (status === 'error') {
    // Always show friendly fixed copy for a redemption failure. The backend error
    // string (e.g. "Invalid or expired hash.") is not user-facing and may carry
    // raw diagnostic noise, so we never render the raw `error` here.
    return (
      <Root>
        <Typography variant="h5">This link can't be opened</Typography>
        <Typography variant="body1">It may have expired or been revoked.</Typography>
      </Root>
    );
  }

  // Gate the redirect on the redux status, not a local flag: the local "setRedeemed"
  // could land after the reducer has already populated hashAuth, but other hooks
  // (useAccessRedirect, useRetrieveData) read the redux state directly. Waiting for
  // status === 'ready' ensures the rest of the app sees the hash session before we
  // hand control over to the canonical content path (otherwise the destination
  // briefly believes it's a normal session with no access -> useAccessRedirect
  // ping-pongs back to the list with an accessAlert, producing a reload loop).
  if (status !== 'ready' || !scope) {
    return (
      <Root>
        <CircularProgress />
        <Typography variant="body2">Opening shared content…</Typography>
      </Root>
    );
  }

  // hasNormalAccess === true means the logged-in user can see this content
  // without the hash; we still hand them off to the canonical path, but the
  // banner won't render (useHashView().active is false in that case).
  return <Redirect to={IN_SCOPE_PATH_FOR(scope)} />;
}
